import React from 'react';
import { Tabs, Tab, Paper, useTheme } from '@mui/material';
import UpgradeType from './UpgradeType';
import BasicInformation from './BasicInformation';
import CDBConversionSettings from './CDBConversionSettings';
import PDBRelocationSettings from './PDBRelocationSettings';
import AdditionalOptions from './AdditionalOptions';

interface UpgradeSectionProps {
  logDir: string;
  setLogDir: (v: string) => void;
  sid: string;
  setSid: (v: string) => void;
  sourceHome: string;
  setSourceHome: (v: string) => void;
  targetHome: string;
  setTargetHome: (v: string) => void;
  platform: string;
  setPlatform: (v: string) => void;
  patchKey: string;
  placeholders: {
    sid: string;
    source_home: string;
    target_home: string;
  };
  restoration: boolean;
  setRestoration: (v: boolean) => void;
  tdeEnabled: boolean;
  setTdeEnabled: (v: boolean) => void;
  tdeKeystorePath: string;
  setTdeKeystorePath: (v: string) => void;
  showKeystore: boolean;
  sourceTnsAdminEnabled: boolean;
  setSourceTnsAdminEnabled: (v: boolean) => void;
  sourceTnsAdminDir: string;
  setSourceTnsAdminDir: (v: string) => void;
  targetTnsAdminEnabled: boolean;
  setTargetTnsAdminEnabled: (v: boolean) => void;
  targetTnsAdminDir: string;
  setTargetTnsAdminDir: (v: string) => void;
  upgradeNodeEnabled: boolean;
  setUpgradeNodeEnabled: (v: boolean) => void;
  upgradeNodeValue: string;
  setUpgradeNodeValue: (v: string) => void;
  upgradeType: string;
  setUpgradeType: (v: string) => void;
  distributedUpgrade: boolean;
  setDistributedUpgrade: (v: boolean) => void;
  targetCdbSid: string;
  setTargetCdbSid: (v: string) => void;
  newPdbName: string;
  setNewPdbName: (v: string) => void;
  fileNameConvertOption: string;
  setFileNameConvertOption: (v: string) => void;
  sourcePath: string;
  setSourcePath: (v: string) => void;
  targetPath: string;
  setTargetPath: (v: string) => void;
  sourcePdb: string;
  setSourcePdb: (v: string) => void;
  targetCdb: string;
  setTargetCdb: (v: string) => void;
  targetPdbName: string;
  setTargetPdbName: (v: string) => void;
  dbLink: string;
  setDbLink: (v: string) => void;
  refreshOption: string;
  setRefreshOption: (v: string) => void;
  refreshInterval: string;
  setRefreshInterval: (v: string) => void;
  activeSubTab: number;
  setActiveSubTab: (v: number) => void;
  startTimeType: 'now' | 'delay' | 'datetime';
  setStartTimeType: (v: 'now' | 'delay' | 'datetime') => void;
  startTimeValue: string;
  setStartTimeValue: (v: string) => void;
  winCredential: string;
  setWinCredential: (v: string) => void;
  deleteWinCredentialFile: boolean;
  setDeleteWinCredentialFile: (v: boolean) => void;
  dropGrpAfterUpgrade: boolean;
  setDropGrpAfterUpgrade: (v: boolean) => void;
  timezoneUpgUpg: boolean;
  setTimezoneUpgUpg: (v: boolean) => void;
  runUtlrpUpg: boolean;
  setRunUtlrpUpg: (v: boolean) => void;
  removeUnderscoreUpg: boolean;
  setRemoveUnderscoreUpg: (v: boolean) => void;
  raiseCompatibleUpg: boolean;
  setRaiseCompatibleUpg: (v: boolean) => void;
  raiseCompatibleUpgValue: string;
  setRaiseCompatibleUpgValue: (v: string) => void;
  delDuringUpgradePfileEnabled: boolean;
  setDelDuringUpgradePfileEnabled: (v: boolean) => void;
  delDuringUpgradePfile: string;
  setDelDuringUpgradePfile: (v: string) => void;
  addAfterUpgradePfileEnabled: boolean;
  setAddAfterUpgradePfileEnabled: (v: boolean) => void;
  addAfterUpgradePfile: string;
  setAddAfterUpgradePfile: (v: string) => void;
  delAfterUpgradePfile: string;
  setDelAfterUpgradePfile: (v: string) => void;
  addDuringUpgradePfile: string;
  setAddDuringUpgradePfile: (v: string) => void;
  proactiveFixups?: boolean;
  setProactiveFixups?: (v: boolean) => void;
  updateCurrentPatchField: (field: string, value: any) => void;
  delAfterUpgradePfileEnabled: boolean;
  setDelAfterUpgradePfileEnabled: (v: boolean) => void;
  addDuringUpgradePfileEnabled: boolean;
  setAddDuringUpgradePfileEnabled: (v: boolean) => void;
  operationType: 'patch' | 'upgrade';
  emTargetNameEnabled: boolean;
  setEmTargetNameEnabled: (v: boolean) => void;
  emTargetName: string;
  setEmTargetName: (v: string) => void;
  emcliPathEnabled: boolean;
  setEmcliPathEnabled: (v: boolean) => void;
  emcliPath: string;
  setEmcliPath: (v: string) => void;
  emBlackoutSuffixEnabled: boolean;
  setEmBlackoutSuffixEnabled: (v: boolean) => void;
  emBlackoutSuffix: string;
  setEmBlackoutSuffix: (v: string) => void;
}

const UpgradeSection: React.FC<UpgradeSectionProps> = ({
  logDir, setLogDir,
  sid, setSid,
  sourceHome, setSourceHome,
  targetHome, setTargetHome,
  platform, setPlatform,
  patchKey, placeholders,
  restoration, setRestoration,
  tdeEnabled, setTdeEnabled,
  tdeKeystorePath, setTdeKeystorePath,
  showKeystore,
  sourceTnsAdminEnabled, setSourceTnsAdminEnabled,
  sourceTnsAdminDir, setSourceTnsAdminDir,
  targetTnsAdminEnabled, setTargetTnsAdminEnabled,
  targetTnsAdminDir, setTargetTnsAdminDir,
  upgradeNodeEnabled, setUpgradeNodeEnabled,
  upgradeNodeValue, setUpgradeNodeValue,
  upgradeType, setUpgradeType,
  distributedUpgrade, setDistributedUpgrade,
  targetCdbSid, setTargetCdbSid,
  newPdbName, setNewPdbName,
  fileNameConvertOption, setFileNameConvertOption,
  sourcePath, setSourcePath,
  targetPath, setTargetPath,
  sourcePdb, setSourcePdb,
  targetCdb, setTargetCdb,
  targetPdbName, setTargetPdbName,
  dbLink, setDbLink,
  refreshOption, setRefreshOption,
  refreshInterval, setRefreshInterval,
  activeSubTab, setActiveSubTab,
  startTimeType,
  setStartTimeType,
  startTimeValue,
  setStartTimeValue,
  winCredential,
  setWinCredential,
  deleteWinCredentialFile,
  setDeleteWinCredentialFile,
  dropGrpAfterUpgrade,
  setDropGrpAfterUpgrade,
  timezoneUpgUpg,
  setTimezoneUpgUpg,
  runUtlrpUpg,
  setRunUtlrpUpg,
  removeUnderscoreUpg,
  setRemoveUnderscoreUpg,
  raiseCompatibleUpg,
  setRaiseCompatibleUpg,
  raiseCompatibleUpgValue,
  setRaiseCompatibleUpgValue,
  delDuringUpgradePfile,
  setDelDuringUpgradePfile,
  addAfterUpgradePfile,
  setAddAfterUpgradePfile,
  delDuringUpgradePfileEnabled,
  setDelDuringUpgradePfileEnabled,
  addAfterUpgradePfileEnabled,
  setAddAfterUpgradePfileEnabled,
  proactiveFixups,
  setProactiveFixups,
  delAfterUpgradePfile,
  setDelAfterUpgradePfile,
  addDuringUpgradePfile,
  setAddDuringUpgradePfile,
  delAfterUpgradePfileEnabled,
  setDelAfterUpgradePfileEnabled,
  addDuringUpgradePfileEnabled,
  setAddDuringUpgradePfileEnabled,
  operationType,
  emTargetNameEnabled,
  setEmTargetNameEnabled,
  emTargetName,
  setEmTargetName,
  emcliPathEnabled,
  setEmcliPathEnabled,
  emcliPath,
  setEmcliPath,
  emBlackoutSuffixEnabled,
  setEmBlackoutSuffixEnabled,
  emBlackoutSuffix,
  setEmBlackoutSuffix,
}) => {
  const theme = useTheme();
  const accentColor = theme.palette.mode === 'dark' ? '#00bcd4' : '#003366';
  
  // Only show CDB Conversion Settings if upgradeType is 'noncdb2cdb'
  // Only show PDB Relocation Settings if upgradeType is 'pdbrelocation'
  const visibleSubTabs = [
    'Upgrade Type',
    'Basic Information',
    ...(upgradeType === 'noncdb2cdb' ? ['CDB Conversion Settings'] : []),
    ...(upgradeType === 'pdbrelocation' ? ['PDB Relocation Settings'] : []),
    'Additional Options',
  ];

  React.useEffect(() => {
    if (activeSubTab >= visibleSubTabs.length) {
      setActiveSubTab(0);
    }
  }, [visibleSubTabs.length, activeSubTab, setActiveSubTab]);

  return (
    <>
      <Tabs
        value={activeSubTab}
        onChange={(_, v) => setActiveSubTab(v)}
        sx={{
          mb: 3,
          minHeight: 44,
          background: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.08)',
          borderRadius: 3,
          px: 1,
          py: 0.5,
          '.MuiTab-root': {
            minHeight: 36,
            minWidth: 120,
            borderRadius: '18px', // Pill shape
            mx: 0.5,
            my: 0.5,
            fontWeight: 500,
            fontSize: 14,
            color: accentColor,
            transition: 'all 0.3s cubic-bezier(.4,0,.2,1)',
            textTransform: 'none',
            background: 'transparent',
            '&:hover': {
              background: theme.palette.mode === 'dark' 
                ? 'rgba(0, 188, 212, 0.08)' 
                : 'rgba(0, 51, 102, 0.05)',
              transform: 'translateY(-1px)',
            },
          },
          '.Mui-selected': {
            color: theme.palette.mode === 'dark' ? '#1a1a1a !important' : '#fff !important',
            fontWeight: 600,
            background: accentColor,
            borderRadius: '18px', // Pill shape
            boxShadow: theme.palette.mode === 'dark'
              ? '0 4px 12px 0 rgba(0, 188, 212, 0.3)'
              : '0 4px 12px 0 rgba(0, 51, 102, 0.2)',
            '&:hover': {
              background: accentColor,
              transform: 'translateY(-1px)',
            },
          },
          '.MuiTabs-indicator': {
            display: 'none', // Hide the underline indicator for pill style
          },
        }}
      >
        {visibleSubTabs.map((label, idx) => (
          <Tab key={label} label={label.replace(/\b\w/g, c => c.toUpperCase())} />
        ))}
      </Tabs>
      <Paper elevation={0} sx={{ p: 2, background: 'background.paper', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
        {visibleSubTabs[activeSubTab] === 'Upgrade Type' && (
          <UpgradeType upgradeType={upgradeType} setUpgradeType={setUpgradeType} />
        )}
        {visibleSubTabs[activeSubTab] === 'Basic Information' && (
          <BasicInformation
            sid={sid}
            setSid={setSid}
            sourceHome={sourceHome}
            setSourceHome={setSourceHome}
            targetHome={targetHome}
            setTargetHome={setTargetHome}
            platform={platform}
            setPlatform={setPlatform}
            placeholders={placeholders}
            patchKey={patchKey}
            logDir={logDir}
            setLogDir={setLogDir}
            winCredential={winCredential}
            setWinCredential={setWinCredential}
            deleteWinCredentialFile={deleteWinCredentialFile}
            setDeleteWinCredentialFile={setDeleteWinCredentialFile}
            operationType={operationType}
          />
        )}
        {visibleSubTabs[activeSubTab] === 'CDB Conversion Settings' && (
          <CDBConversionSettings
            sid={sid}
            targetCdbSid={targetCdbSid}
            setTargetCdbSid={setTargetCdbSid}
            newPdbName={newPdbName}
            setNewPdbName={setNewPdbName}
            fileNameConvertOption={fileNameConvertOption}
            setFileNameConvertOption={setFileNameConvertOption}
            sourcePath={sourcePath}
            setSourcePath={setSourcePath}
            targetPath={targetPath}
            setTargetPath={setTargetPath}
            platform={platform}
          />
        )}
        {visibleSubTabs[activeSubTab] === 'PDB Relocation Settings' && (
          <PDBRelocationSettings
            sourcePdb={sourcePdb}
            setSourcePdb={setSourcePdb}
            targetCdb={targetCdb}
            setTargetCdb={setTargetCdb}
            targetPdbName={targetPdbName}
            setTargetPdbName={setTargetPdbName}
            dbLink={dbLink}
            setDbLink={setDbLink}
            refreshOption={refreshOption}
            setRefreshOption={setRefreshOption}
            refreshInterval={refreshInterval}
            setRefreshInterval={setRefreshInterval}
            sourcePath={sourcePath}
            setSourcePath={setSourcePath}
            targetPath={targetPath}
            setTargetPath={setTargetPath}
            fileNameConvertOption={fileNameConvertOption}
            setFileNameConvertOption={setFileNameConvertOption}
            platform={platform}
            startTimeType={startTimeType}
            setStartTimeType={setStartTimeType}
            startTimeValue={startTimeValue}
            setStartTimeValue={setStartTimeValue}
            sid={sid}
          />
        )}
        {visibleSubTabs[activeSubTab] === 'Additional Options' && (
          <AdditionalOptions
            restoration={restoration}
            setRestoration={setRestoration}
            tdeEnabled={tdeEnabled}
            setTdeEnabled={setTdeEnabled}
            tdeKeystorePath={tdeKeystorePath}
            setTdeKeystorePath={setTdeKeystorePath}
            showKeystore={showKeystore}
            sourceTnsAdminEnabled={sourceTnsAdminEnabled}
            setSourceTnsAdminEnabled={setSourceTnsAdminEnabled}
            sourceTnsAdminDir={sourceTnsAdminDir}
            setSourceTnsAdminDir={setSourceTnsAdminDir}
            targetTnsAdminEnabled={targetTnsAdminEnabled}
            setTargetTnsAdminEnabled={setTargetTnsAdminEnabled}
            targetTnsAdminDir={targetTnsAdminDir}
            setTargetTnsAdminDir={setTargetTnsAdminDir}
            upgradeNodeEnabled={upgradeNodeEnabled}
            setUpgradeNodeEnabled={setUpgradeNodeEnabled}
            upgradeNodeValue={upgradeNodeValue}
            setUpgradeNodeValue={setUpgradeNodeValue}
            upgradeType={upgradeType}
            distributedUpgrade={distributedUpgrade}
            setDistributedUpgrade={setDistributedUpgrade}
            proactiveFixups={proactiveFixups}
            setProactiveFixups={setProactiveFixups}
            startTimeType={startTimeType}
            setStartTimeType={setStartTimeType}
            startTimeValue={startTimeValue}
            setStartTimeValue={setStartTimeValue}
            platform={platform}
            dropGrpAfterUpgrade={dropGrpAfterUpgrade}
            setDropGrpAfterUpgrade={setDropGrpAfterUpgrade}
            timezoneUpgUpg={timezoneUpgUpg}
            setTimezoneUpgUpg={setTimezoneUpgUpg}
            runUtlrpUpg={runUtlrpUpg}
            setRunUtlrpUpg={setRunUtlrpUpg}
            removeUnderscoreUpg={removeUnderscoreUpg}
            setRemoveUnderscoreUpg={setRemoveUnderscoreUpg}
            raiseCompatibleUpg={raiseCompatibleUpg}
            setRaiseCompatibleUpg={setRaiseCompatibleUpg}
            raiseCompatibleUpgValue={raiseCompatibleUpgValue}
            setRaiseCompatibleUpgValue={setRaiseCompatibleUpgValue}
            delDuringUpgradePfileEnabled={delDuringUpgradePfileEnabled}
            setDelDuringUpgradePfileEnabled={setDelDuringUpgradePfileEnabled}
            delDuringUpgradePfile={delDuringUpgradePfile}
            setDelDuringUpgradePfile={setDelDuringUpgradePfile}
            addAfterUpgradePfileEnabled={addAfterUpgradePfileEnabled}
            setAddAfterUpgradePfileEnabled={setAddAfterUpgradePfileEnabled}
            addAfterUpgradePfile={addAfterUpgradePfile}
            setAddAfterUpgradePfile={setAddAfterUpgradePfile}
            delAfterUpgradePfileEnabled={delAfterUpgradePfileEnabled}
            setDelAfterUpgradePfileEnabled={setDelAfterUpgradePfileEnabled}
            delAfterUpgradePfile={delAfterUpgradePfile}
            setDelAfterUpgradePfile={setDelAfterUpgradePfile}
            addDuringUpgradePfileEnabled={addDuringUpgradePfileEnabled}
            setAddDuringUpgradePfileEnabled={setAddDuringUpgradePfileEnabled}
            addDuringUpgradePfile={addDuringUpgradePfile}
            setAddDuringUpgradePfile={setAddDuringUpgradePfile}
            emTargetNameEnabled={emTargetNameEnabled}
            setEmTargetNameEnabled={setEmTargetNameEnabled}
            emTargetName={emTargetName}
            setEmTargetName={setEmTargetName}
            emcliPathEnabled={emcliPathEnabled}
            setEmcliPathEnabled={setEmcliPathEnabled}
            emcliPath={emcliPath}
            setEmcliPath={setEmcliPath}
            emBlackoutSuffixEnabled={emBlackoutSuffixEnabled}
            setEmBlackoutSuffixEnabled={setEmBlackoutSuffixEnabled}
            emBlackoutSuffix={emBlackoutSuffix}
            setEmBlackoutSuffix={setEmBlackoutSuffix}
            oemSidPlaceholder={sid || placeholders.sid || 'DB1'}
          />
        )}
      </Paper>
    </>
  );
};

export default UpgradeSection; 